import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DelegationModal = ({ document, onDelegate, onCancel }) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [reason, setReason] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notifyUser, setNotifyUser] = useState(true);

  const availableUsers = [
    {
      value: 'michael.rodriguez@enterprise.com',
      label: 'Michael Rodriguez',
      description: 'Solution Architect'
    },
    {
      value: 'sarah.chen@enterprise.com',
      label: 'Sarah Chen',
      description: 'Business Analyst'
    },
    {
      value: 'david.kim@enterprise.com',
      label: 'David Kim',
      description: 'Technical Lead'
    },
    {
      value: 'jennifer.walsh@enterprise.com',
      label: 'Jennifer Walsh',
      description: 'Project Manager'
    },
    {
      value: 'robert.johnson@enterprise.com',
      label: 'Robert Johnson',
      description: 'Enterprise Architect'
    }
  ];

  const handleDelegate = () => {
    if (selectedUser && reason?.trim()) {
      onDelegate(document?.id, {
        delegatedTo: selectedUser,
        reason: reason?.trim(),
        expiryDate: expiryDate || null,
        notifyUser,
        timestamp: new Date()?.toISOString()
      });
    }
  };

  const canSubmit = () => {
    return selectedUser && reason?.trim()?.length > 0;
  };

  // Set default expiry date to 7 days from now
  React.useEffect(() => {
    const defaultExpiry = new Date();
    defaultExpiry?.setDate(defaultExpiry?.getDate() + 7);
    setExpiryDate(defaultExpiry?.toISOString()?.split('T')?.[0]);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="UserCheck" size={24} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Delegate Approval
              </h2>
              <p className="text-sm text-muted-foreground">
                {document?.title}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onCancel}
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Document Info */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon 
                name={document?.type === 'BPD' ? 'FileText' : 'FileCode'} 
                size={20} 
                className="text-primary"
              />
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {document?.title}
                </h3>
                <p className="text-xs text-muted-foreground">
                  Version {document?.version} • Due {new Date(document.deadline)?.toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Delegate To */}
          <Select
            label="Delegate to"
            description="Select a user to delegate this approval to"
            placeholder="Choose a user..."
            options={availableUsers}
            value={selectedUser}
            onChange={setSelectedUser}
            searchable
            required
          />

          {/* Reason */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Reason for delegation <span className="text-destructive">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              placeholder="Explain why you're delegating this approval..."
              className="w-full p-3 border border-border rounded-lg bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              rows={3}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              This reason will be included in the audit trail
            </p>
          </div>

          {/* Expiry Date */}
          <Input
            label="Delegation expires"
            type="date"
            description="The delegation will automatically expire on this date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e?.target?.value)}
            min={new Date()?.toISOString()?.split('T')?.[0]}
          />

          {/* Notification Options */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">
              Notification Options
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="notifyUser"
                checked={notifyUser}
                onChange={(e) => setNotifyUser(e?.target?.checked)}
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-ring"
              />
              <label htmlFor="notifyUser" className="text-sm text-foreground">
                Send email notification to delegated user
              </label>
            </div>
          </div>

          {/* Delegation Details */}
          <div className="p-4 bg-muted/20 rounded-lg">
            <h4 className="text-sm font-medium text-foreground mb-3">
              Delegation Details
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Current approver:</span>
                <span className="text-foreground font-medium">You</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Will be delegated to:</span>
                <span className="text-foreground font-medium">
                  {selectedUser ? availableUsers?.find(u => u?.value === selectedUser)?.label : 'Not selected'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delegation expires:</span>
                <span className="text-foreground font-medium">
                  {expiryDate ? new Date(expiryDate)?.toLocaleDateString() : 'Not set'}
                </span>
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-warning mb-1">Important Notice</p>
                <p className="text-muted-foreground">
                  Once delegated, the selected user will have full approval authority for this document. 
                  You can revoke this delegation at any time before it expires.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Shield" size={16} />
            <span>Delegation will be logged in audit trail</span>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              iconName="UserCheck"
              iconPosition="left"
              onClick={handleDelegate}
              disabled={!canSubmit()}
            >
              Delegate Approval
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DelegationModal;